import Tesseract from 'tesseract.js';
import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker
if (typeof window !== 'undefined') {
  pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
}

// ============================================================================
// PDF TO IMAGE CONVERSION
// ============================================================================

/**
 * Convert PDF first page to image
 */
async function pdfToImage(file) {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const page = await pdf.getPage(1);
    
    const viewport = page.getViewport({ scale: 2.0 });
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.height = viewport.height;
    canvas.width = viewport.width;
    
    await page.render({
      canvasContext: context,
      viewport: viewport
    }).promise;
    
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/png');
    });
  } catch (error) {
    console.error('PDF conversion error:', error);
    throw new Error('Failed to read PDF file');
  }
}

// ============================================================================
// PREPROCESSING
// ============================================================================

/**
 * Preprocess image for better OCR
 */
async function preprocessImage(imageBlob, strategy = 'moderate') {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const targetWidth = Math.max(img.width, 2480); // ~300 DPI
      const scale = targetWidth / img.width;
      
      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = img.height * scale;
      const ctx = canvas.getContext('2d');
      
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      // Apply strategy-specific preprocessing
      const contrast = strategy === 'aggressive' ? 1.8 : 1.3;
      const applyThreshold = strategy === 'aggressive';
      
      for (let i = 0; i < data.length; i += 4) {
        const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        
        // Contrast enhancement
        const factor = (259 * (contrast + 255)) / (255 * (259 - contrast));
        let enhanced = factor * (gray - 128) + 128;
        enhanced = Math.max(0, Math.min(255, enhanced));
        
        // Optional binarization
        if (applyThreshold) {
          const threshold = 140;
          enhanced = enhanced > threshold ? 255 : 0;
        }
        
        data[i] = data[i + 1] = data[i + 2] = enhanced;
      }
      
      ctx.putImageData(imageData, 0, 0);
      
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/png');
    };
    
    img.src = URL.createObjectURL(imageBlob);
  });
}

// ============================================================================
// OCR WITH TSV/LAYOUT DATA
// ============================================================================

/**
 * Run OCR with TSV output for line positions
 */
async function ocrWithTSV(imageBlob, options = {}) {
  const result = await Tesseract.recognize(
    imageBlob,
    'eng',
    {
      workerPath: 'https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/worker.min.js',
      corePath: 'https://cdn.jsdelivr.net/npm/tesseract.js-core@5/tesseract-core.wasm.js',
      tessedit_pageseg_mode: options.psm || 3,
      tessedit_create_tsv: 1,
      preserve_interword_spaces: 1,
      ...options.config
    }
  );
  
  return {
    text: result.data.text,
    confidence: result.data.confidence,
    tsv: result.data.tsv,
    lines: result.data.lines || []
  };
}

/**
 * OCR a specific region of the image
 */
async function ocrRegion(imageBlob, y, height, options = {}) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = async () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      canvas.width = img.width;
      canvas.height = height;
      ctx.drawImage(img, 0, y, img.width, height, 0, 0, img.width, height);
      
      canvas.toBlob(async (blob) => {
        const result = await Tesseract.recognize(
          blob,
          'eng',
          {
            workerPath: 'https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/worker.min.js',
            corePath: 'https://cdn.jsdelivr.net/npm/tesseract.js-core@5/tesseract-core.wasm.js',
            tessedit_pageseg_mode: options.psm || 6,
            tessedit_char_whitelist: options.whitelist || 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789/&()- ',
            ...options.config
          }
        );
        
        resolve({
          text: result.data.text,
          confidence: result.data.confidence
        });
      }, 'image/png');
    };
    
    img.src = URL.createObjectURL(imageBlob);
  });
}

// ============================================================================
// DOCUMENT CLASSIFICATION
// ============================================================================

/**
 * Classify document type
 * Priority: Check for full qualification indicators first (stronger signal)
 */
function classifyDocument(text) {
  // Full qualification indicators (CHECK FIRST - strongest signal)
  if (/in\s+completion\s+of\s+the\s+following\s+qualification/i.test(text)) return 'qualification';
  if (/certificate\s+(?:iv|iii|ii|i)\s+in/i.test(text)) return 'qualification';
  
  // Statement of Attainment indicators (weaker signal)
  if (/statement\s+of\s+attainment/i.test(text)) return 'soa';
  if (/these\s+competencies\s+form\s+part\s+of/i.test(text)) return 'soa';
  if (/units?\s+of\s+competency/i.test(text)) return 'soa';
  
  // License/ticket
  if (/license|licence|ticket/i.test(text)) return 'license';
  
  // Default to qualification if we see generic certificate language
  if (/certificate|qualification/i.test(text)) return 'qualification';
  
  return 'unknown';
}

// ============================================================================
// CERTIFICATE NUMBER EXTRACTION (EARLY SEPARATION)
// ============================================================================

/**
 * Extract certificate number (different from qualification code)
 */
function extractCertNumber(text) {
  const patterns = [
    /certificate\s*(?:no|number)\.?\s*[:.]?\s*([A-Z0-9-]+)/i,
    /cert\.?\s*no\.?\s*[:.]?\s*([A-Z0-9-]+)/i
  ];
  
  for (const pattern of patterns) {
    const m = text.match(pattern);
    if (m) {
      console.log('Found certificate number:', m[1]);
      return m[1].trim().toUpperCase();
    }
  }
  
  return null;
}

/**
 * Find qualification code (not certificate number)
 */
function findQualCode(text) {
  const re = /\b[A-Z]{2,6}[\s_]*\d{4,5}[A-Z]?\b/g; // eslint-disable-line no-useless-escape
  let m;
  while ((m = re.exec(text))) {
    // Check context - skip if near "certificate no"
    const ctx = text.slice(Math.max(0, m.index - 25), m.index + 25);
    if (!/certificate\s*no\.?/i.test(ctx)) {
      return m[0].replace(/[\s_-]+/g, '').toUpperCase();
    }
  }
  return null;
}

// ============================================================================
// STATEMENT OF ATTAINMENT EXTRACTION
// ============================================================================

const SOA_ANCHORS = [
  /statement\s+of\s+attainment/i,
  /these\s+competencies\s+form\s+part\s+of/i,
  /units?\s+of\s+competency/i,
  /schedule\s+\d+/i  // Schedule-based courses
];

/**
 * Extract name from Statement of Attainment
 */
function extractSoAName(text) {
  // Pattern 1: "These competencies form part of [NAME]"
  const m = text.match(/these\s+competencies\s+form\s+part\s+of\s+([^\n]+)/i);
  if (m) {
    const name = m[1].trim();
    console.log('SoA name from "form part of":', name);
    return name;
  }
  
  // Pattern 2: Schedule [number] [description] format
  const scheduleMatch = text.match(/schedule\s+(\d+)\s+([A-Za-z][\s\w]+?)(?=\n|this\s+is\s+a\s+statement|has\s+attained)/i);
  if (scheduleMatch) {
    const name = `Schedule ${scheduleMatch[1]} ${scheduleMatch[2].trim()}`;
    console.log('SoA name from Schedule format:', name);
    return name;
  }
  
  // Pattern 3: Window after any SoA anchor
  for (const anchor of SOA_ANCHORS) {
    const am = text.match(anchor);
    if (!am) continue;
    
    const win = text.slice(am.index + am[0].length).split('\n').slice(0, 8);
    
    // Look for qualification code + description
    const qual = win.find(l => /^[A-Z]{2,6}\s*\d{4,5}[A-Z]?\s+.+/.test(l));
    if (qual) {
      console.log('SoA name from qualification line:', qual.trim());
      return qual.trim();
    }
    
    // Look for training package
    const pkg = win.find(l => /(training\s+package)/i.test(l));
    if (pkg) {
      console.log('SoA name from package line:', pkg.trim());
      return pkg.trim();
    }
  }
  
  return null;
}

// ============================================================================
// QUALIFICATION EXTRACTION
// ============================================================================

/**
 * Extract qualification name (Certificate II/III/IV etc)
 */
function extractQualificationName(text) {
  const LEVEL = '(?:X|IX|VIII|VII|VI|V|IV|III|II|I|[1-9]|10)';
  
  // Tolerant pattern for OCR errors (cer[t7]if[i1]cate)
  const QUAL_BLOCK = new RegExp(
    String.raw`cer[t7][i1l]f[i1]cate\s+(${LEVEL})\s+(?:\n|\s){0,8}in\s+` +
    String.raw`([A-Za-z][A-Za-z/&()
 -]+?)(?=\s+[A-Z]{2,6}[\s_]*\d{4,5}[A-Z]?)\s+` +
    String.raw`(?!no\.?\s*)([A-Z]{2,6}[\s_]*\d{4,5}[A-Z]?)`,
    'i'
  );
  
  const m = text.match(QUAL_BLOCK);
  if (m) {
    const level = m[1];
    const field = m[2].replace(/[\n\r]+/g, ' ').replace(/\s{2,}/g, ' ').trim();
    const code = m[3].replace(/[\s_-]+/g, '').toUpperCase();
    const name = `Certificate ${level} in ${field} ${code}`;
    console.log('Qualification name extracted:', name);
    return name;
  }
  
  return null;
}

// ============================================================================
// MAIN EXTRACTION WITH CLASSIFICATION
// ============================================================================

/**
 * Extract text using classification-based approach
 */
export async function extractTextFromFile(file, onProgress = null) {
  try {
    let imageFile = file;
    
    // Convert PDF to image first
    if (file.type === 'application/pdf') {
      if (onProgress) onProgress(5);
      imageFile = await pdfToImage(file);
      if (onProgress) onProgress(10);
    }
    
    console.log('Starting OCR with moderate preprocessing...');
    
    // Preprocess and run initial OCR
    if (onProgress) onProgress(15);
    const preprocessed = await preprocessImage(imageFile, 'moderate');
    
    if (onProgress) onProgress(30);
    const result = await ocrWithTSV(preprocessed, { psm: 3 });
    
    if (onProgress) onProgress(70);
    
    console.log('Initial OCR confidence:', result.confidence);
    console.log('Text sample:', result.text.substring(0, 300));
    
    // Classify document
    const docType = classifyDocument(result.text);
    console.log('Document classified as:', docType);
    
    let mergedText = result.text;
    let needsReOCR = false;
    
    // Check if we need re-OCR
    if (docType === 'qualification') {
      const hasCertificatePattern = /certificate\s+(?:iv|iii|ii|i)/i.test(result.text);
      // Re-OCR if pattern missing OR low confidence
      if (!hasCertificatePattern || result.confidence < 85) {
        needsReOCR = true;
        console.log('Certificate pattern missing or low confidence - will re-OCR');
      }
    }
    
    // Re-OCR if needed
    if (needsReOCR) {
      console.log('Running targeted re-OCR...');
      
      // Find anchor line position
      const anchorMatch = result.text.match(/in\s+completion\s+of\s+the\s+following\s+qualification/i);
      
      if (anchorMatch && result.lines.length > 0) {
        // Find the line containing the anchor
        const anchorLine = result.lines.find(line => 
          /in\s+completion\s+of\s+the\s+following\s+qualification/i.test(line.text)
        );
        
        if (anchorLine && anchorLine.bbox) {
          const anchorY = anchorLine.bbox.y1;
          console.log('Anchor line found at Y:', anchorY);
          
          if (onProgress) onProgress(85);
          
          // Re-OCR 400px band below anchor
          const bandResult = await ocrRegion(preprocessed, anchorY, 400, {
            psm: 6,
            whitelist: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789/&()- '
          });
          
          console.log('Band OCR result:', bandResult.text.substring(0, 200));
          console.log('Band confidence:', bandResult.confidence);
          
          // Merge if we found certificate text
          if (/certificate\s+(?:iv|iii|ii|i)/i.test(bandResult.text)) {
            mergedText = result.text + '\n' + bandResult.text;
            console.log('Band OCR successful - merged text');
          }
        }
      } else {
        // No anchor found, try aggressive preprocessing on full image
        console.log('No anchor found, trying aggressive preprocessing...');
        
        if (onProgress) onProgress(85);
        const aggressive = await preprocessImage(imageFile, 'aggressive');
        const aggResult = await ocrWithTSV(aggressive, { psm: 3 });
        
        console.log('Aggressive OCR confidence:', aggResult.confidence);
        
        if (aggResult.confidence > result.confidence) {
          mergedText = aggResult.text;
          console.log('Using aggressive OCR result');
        }
      }
    }
    
    if (onProgress) onProgress(100);
    
    return {
      text: mergedText,
      confidence: result.confidence,
      docType: docType,
      lowConfidence: result.confidence < 70
    };
    
  } catch (error) {
    console.error('OCR extraction error:', error);
    throw new Error('Failed to read certificate. Please enter details manually.');
  }
}

// ============================================================================
// PARSING
// ============================================================================

/**
 * Normalize OCR text
 */
function normalise(text) {
  return text
    .replace(/\r\n?/g, '\n')
    .replace(/[ \t]+/g, ' ')
    .replace(/-\n(?=[a-z])/gi, '')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/[^\S\n]+\n/g, '\n')
    .trim();
}

/**
 * Fix common OCR glitches in unit codes
 */
function fixUnitCodeGlitches(code) {
  let c = code.replace(/[\s_-]+/g, '');
  const m = c.match(/^([A-Z]{3,10})(\d{2,5})([A-Z]?)$/i);
  if (!m) return c.toUpperCase();
  // eslint-disable-next-line no-unused-vars
  let [_unused, letters, digits, end] = m;
  digits = digits.replace(/O/g, '0').replace(/I/g, '1').replace(/l/g, '1');
  return (letters + digits + (end || '')).toUpperCase();
}

/**
 * Extract unit codes and descriptions
 */
function extractUnits(text) {
  const units = [];
  
  const UNIT_CODE_LOOSE = String.raw`([A-Z]{3,10})[\s_]*([0-9]{2,5})[\s_]*([A-Z]?)`;
  // eslint-disable-next-line no-useless-escape
  const UNIT_LINE_RE = new RegExp(
    String.raw`(?:^|\n)${UNIT_CODE_LOOSE}[ \t]+([^\n]+)`,
    'gi'
  );
  
  let m;
  while ((m = UNIT_LINE_RE.exec(text)) !== null) {
    const code = fixUnitCodeGlitches(`${m[1]}${m[2]}${m[3]}`);
    let desc = m[4].trim().replace(/[ ]{2,}/g, ' ');
    
    desc = desc.replace(/^[—–_-]+\s*/, '');
    
    if (!desc || !/[A-Za-z]/.test(desc)) continue;
    if (/^[A-Z]{3}\d{5}$/.test(code)) continue;
    if (/Certificate\s+[IVX]+\s+in/i.test(desc)) continue;
    if (/Training Package/i.test(desc)) continue;
    if (/^Schedule/i.test(code)) continue;
    if (/Statutory|Supervisor|Course/i.test(desc)) continue;
    if (/^PTY\s+LTD$/i.test(desc)) continue;
    if (!/[a-z]/.test(desc)) continue;
    
    units.push({ code, description: desc });
  }
  
  const seen = new Set();
  return units.filter(u => {
    const k = u.code + '|' + u.description.toLowerCase();
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

/**
 * Extract certificate name based on document type
 */
function extractCertificateName(text, docType) {
  let name = null;
  
  if (docType === 'soa') {
    name = extractSoAName(text);
  } else if (docType === 'qualification') {
    name = extractQualificationName(text);
  }
  
  // Fallback synthesis if still no name
  if (!name) {
    const LEVEL = '(?:X|IX|VIII|VII|VI|V|IV|III|II|I|[1-9]|10)';
    const code = findQualCode(text);
    const lvlMatch = text.match(new RegExp(`cer[t7][i1l]f[i1]cate\\s+(${LEVEL})`, 'i'));
    const lvl = lvlMatch ? lvlMatch[1] : null;
    
    if (lvl && code) {
      name = `Certificate ${lvl} (${code})`;
      console.log('Synthesized name from level + code:', name);
    }
  }
  
  return name;
}

/**
 * Extract dates with ordinal format first
 */
function extractDates(text) {
  // Ordinal format: "this 10th day of August 2010" - CHECK FIRST
  const ISSUE_ORDINAL_RE = /this\s+(\d{1,2})(?:st|nd|rd|th)?\s+day\s+of\s+([A-Za-z]{3,9})\s+(\d{4})/i;
  const ordMatch = text.match(ISSUE_ORDINAL_RE);
  if (ordMatch) {
    const [, day, month, year] = ordMatch;
    const issueDate = toISOish(`${day} ${month} ${year}`);
    if (issueDate) {
      console.log('Date from ordinal format:', issueDate);
      return { issueDate, expiryDate: null };
    }
  }
  
  // Standard date formats
  // eslint-disable-next-line no-useless-escape
  const DATE_RE = new RegExp(
    String.raw`(?:Date\s*(?:of)?\s*(?:Issue|Completion|Completed|Issued)?\s*:?\s*)(\d{1,2}(?:[/\- ](?:[A-Za-z]{3,9}|\d{1,2}))(?:[/\- ]\d{2,4}))`,
    'gi'
  );
  
  // eslint-disable-next-line no-useless-escape
  const EXPIRY_RE = /(?:Expiry|Expiration|Valid\s*To|Valid\s*Until)\s*:?\s*(\d{1,2}(?:[/\- ](?:[A-Za-z]{3,9}|\d{1,2}))(?:[/\- ]\d{2,4}))/i;

  let issue = null;
  let m;
  while ((m = DATE_RE.exec(text)) !== null) {
    if (!issue) issue = toISOish(m[1]);
  }
  
  const expiryMatch = text.match(EXPIRY_RE);
  const expiry = expiryMatch ? toISOish(expiryMatch[1]) : null;
  
  return { issueDate: issue, expiryDate: expiry };
}

/**
 * Convert date string to ISO format
 */
function toISOish(dateStr) {
  try {
    const s = dateStr.trim().replace(/\s+/g, ' ');
    
    const dashMonMatch = s.match(/(\d{1,2})[-\s](Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[-\s](\d{2,4})/i);
    if (dashMonMatch) {
      const [, day, month, yearStr] = dashMonMatch;
      let year = parseInt(yearStr);
      if (year < 100) {
        year = year < 50 ? 2000 + year : 1900 + year;
      }
      const monthMap = {
        jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
        jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11
      };
      const monthNum = monthMap[month.toLowerCase().slice(0, 3)];
      const date = new Date(year, monthNum, parseInt(day));
      return formatDate(date);
    }
    
    const dmyMatch = s.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (dmyMatch) {
      const [, day, month, year] = dmyMatch;
      const date = new Date(year, month - 1, day);
      return formatDate(date);
    }
    
    const date = new Date(s);
    if (!isNaN(date.getTime())) {
      return formatDate(date);
    }
  } catch (error) {
    console.error('Date parsing error:', error);
  }
  
  return dateStr;
}

/**
 * Format date as YYYY-MM-DD
 */
function formatDate(date) {
  if (!date || isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Detect certificate type
 */
function detectCertificateType(text, docType) {
  // Use docType if it's specific
  if (docType === 'license') return 'License';
  
  // Check for Certificate levels
  if (/certificate\s+iv/i.test(text)) return 'Certificate IV';
  if (/certificate\s+iii/i.test(text)) return 'Certificate III';
  if (/certificate\s+ii/i.test(text)) return 'Certificate II';
  if (/certificate\s+i(?:\s|$)/i.test(text)) return 'Certificate I';
  
  const textLower = text.toLowerCase();
  
  const typeKeywords = {
    'License': ['license', 'licence', 'driver'],
    'Safety': ['safety', 'whs', 'ohs', 'work health'],
    'First Aid': ['first aid', 'cpr', 'emergency'],
    'Training': ['training', 'attainment', 'competency'],
    'Qualification': ['diploma', 'degree', 'qualification']
  };
  
  for (const [type, keywords] of Object.entries(typeKeywords)) {
    if (keywords.some(keyword => textLower.includes(keyword))) {
      return type;
    }
  }
  
  return 'Other';
}

/**
 * Parse certificate information from extracted text
 */
export function parseCertificateInfo(extractResult) {
  const text = typeof extractResult === 'string' ? extractResult : extractResult.text;
  const docType = extractResult.docType || 'unknown';
  const lowConfidence = extractResult.lowConfidence || false;
  
  const normalisedText = normalise(text);
  
  console.log('=== PARSING START ===');
  console.log('Document type:', docType);
  console.log('Text sample:', normalisedText.substring(0, 300));
  
  // Extract certificate number FIRST (to exclude from code search)
  const certificateNumber = extractCertNumber(normalisedText);
  
  // Extract components (guarded to prevent null overwrites)
  const units = extractUnits(normalisedText);
  const certificateName = extractCertificateName(normalisedText, docType);
  const { issueDate, expiryDate } = extractDates(normalisedText);
  const type = detectCertificateType(normalisedText, docType);
  
  console.log('=== EXTRACTION RESULTS ===');
  console.log('- Certificate number:', certificateNumber);
  console.log('- Certificate name:', certificateName);
  console.log('- Units:', units.length);
  console.log('- Type:', type);
  console.log('- Issue date:', issueDate);
  console.log('- Expiry date:', expiryDate);
  
  // Build result with guarded assignments (only set if we have data)
  const info = {
    certificateNumber: certificateNumber || null,
    type: type || 'Other',
    issueDate: issueDate || null,
    expiryDate: expiryDate || null,
    lowConfidence: lowConfidence,
    confidence: 0
  };
  
  // Only set name if we found one (don't overwrite with null)
  if (certificateName) {
    info.name = certificateName;
    info.certificateName = certificateName;
  }
  
  // Only set units if we found any (don't overwrite with empty array)
  if (units && units.length > 0) {
    info.units = units.map(u => ({
      code: u.code,
      name: u.description.substring(0, 255)
    }));
  }
  
  // Fallback: if no name but we have units, use first unit
  if (!info.name && info.units && info.units.length > 0) {
    const firstUnit = info.units[0];
    info.name = `${firstUnit.code} ${firstUnit.name}`;
    info.certificateName = info.name;
  }
  
  // Calculate confidence
  let foundItems = 0;
  if (info.certificateName) foundItems++;
  if (info.units && info.units.length > 0) foundItems++;
  if (info.type && info.type !== 'Other') foundItems++;
  if (info.issueDate) foundItems++;
  
  info.confidence = (foundItems / 4) * 100;
  
  console.log('=== FINAL CONFIDENCE ===');
  console.log('Score:', info.confidence);
  console.log('Low confidence flag:', info.lowConfidence);
  
  return info;
}

/**
 * Main function to extract and parse certificate
 */
export async function processCertificate(file, onProgress = null) {
  const extractResult = await extractTextFromFile(file, onProgress);
  
  const info = parseCertificateInfo(extractResult);
  
  return {
    ...info,
    rawText: extractResult.text || extractResult
  };
}
